// src/pages/WorkOrder/components/DetailServiceTypeDialog.jsx
import { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Typography,
  Box,
  Stack
} from '@mui/material';
import { useCompanyList } from '../../../../stores/companyStore';
import { useWorkshopList } from '../../../../stores/workshopStore';
import { isZeroDate } from '../../../../utils/dateTimeUtils';
import dayjs from 'dayjs';
import { useLanguage } from '../../../../utils/i18n';

const fmtDateID = (iso) => {
  if (isZeroDate(iso)) return '';
  return dayjs(iso).format('DD MMM YYYY');
};

const fmtRp = (v) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (isNaN(n)) return '-';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);
};

export default function DetailWorkOrderDialog({ open, onClose, data = {} }) {
  const { t } = useLanguage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const services = Array.isArray(data?.vm_service_type) ? data.vm_service_type : [];

  const { companyOptions: companyOptionsFromHook = [] } = useCompanyList();
  const { data: workshopList = [] } = useWorkshopList(0, 1000, '');

  const companyMap = useMemo(() => {
    const m = new Map();
    (companyOptionsFromHook || []).forEach((c) => {
      const key = String(c.value ?? c.id ?? c);
      const label = c.label ?? c.name ?? key;
      m.set(key, label);
    });
    return m;
  }, [companyOptionsFromHook]);

  const workshopMap = useMemo(() => {
    const m = new Map();
    (workshopList || []).forEach((w) => {
      const key = String(w.id ?? w.value ?? w);
      const label = w.name ?? w.workshop_name ?? key;
      m.set(key, label);
    });
    return m;
  }, [workshopList]);

  const companyLabel = (val) => {
    if (val == null || val === '') return '-';
    const k = String(val);
    return companyMap.get(k) ?? k;
  };

  const workshopLabel = (val) => {
    if (val == null || val === '') return '-';
    const k = String(val);
    return workshopMap.get(k) ?? k;
  };

  const totalPrice = useMemo(() => {
    return services.reduce((acc, s) => acc + (Number(s.price || s.service_cost) || 0), 0);
  }, [services]);

  const displayStatus = (status) => {
    if (status === 'ON PROGRESS') return t('workOrder.statusOnProgress', 'ON PROGRESS');
    if (status === 'COMPLETED') return t('workOrder.statusCompleted', 'COMPLETED');
    return status ?? '-';
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('workOrder.completeWo', 'Complete WO')}</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%' }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Stack>
                <Typography variant="body1">{t('workOrder.status', 'Status')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {displayStatus(data?.status)}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('general.company', 'Company')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {companyLabel(data?.company)}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('workOrder.invoiceNumber', 'Invoice Number')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {data?.invoice_number ?? '-'}
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Stack>
                <Typography variant="body1">{t('workOrder.woDate', 'WO Date')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {fmtDateID(data?.vm_date ?? '-')}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('workOrder.vehicle', 'Vehicle')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {data?.license_plate ?? '-'}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('workOrder.workshop', 'Workshop')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {workshopLabel(data?.workshop)}
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Stack>
                <Typography variant="body1" sx={{ color: 'transparent' }}>
                  -
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'transparent' }}>
                  -
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('workOrder.estimatedCompletionDate', 'Estimated Completion Date')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {fmtDateID(data?.date_completed ?? '-')}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('workOrder.currentOdo', 'Current Odo')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {data?.current_odometer ?? '-'}
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Stack>
                <Typography variant="body1" sx={{ color: 'transparent' }}>
                  -
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'transparent' }}>
                  -
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('workOrder.actualCompletionDate', 'Actual Completion Date')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {fmtDateID(data?.date_completed_actual ?? '-')}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1">{t('workOrder.actualOdo', 'Actual Odo')}:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {data?.actual_odometer ?? '-'}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Box>

        {services.length === 0 ? (
          <Typography>{t('workOrder.noServiceTypeFound', 'No service type found')}</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('workOrder.task', 'Task')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('workOrder.scheduleBy', 'Schedule By')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('workOrder.nextServiceOdo', 'Next Service Odo')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('workOrder.nextServiceDate', 'Next Service Date')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('workOrder.remark', 'Remark')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {t('workOrder.serviceCost', 'Service Cost')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {services.map((s) => (
                <TableRow key={s.id ?? s.tempId}>
                  <TableCell>{s.service_type_name ?? s.join?.service_type_name ?? '-'}</TableCell>
                  <TableCell>{s.schedule_by ?? '-'}</TableCell>
                  <TableCell>{s.next_service_odo ?? '-'}</TableCell>
                  <TableCell>{fmtDateID(s.next_service_date)}</TableCell>
                  <TableCell>{s.remarks ?? '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {fmtRp(s.price ?? s.service_cost)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} />
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>{t('workOrder.totalPrice', 'Total Price')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmtRp(totalPrice)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('general.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
